/**
 * Pro Workout Templates (DEV-only scaffold)
 *
 * Doctrine: TRAINER_ROLE_SPEC.md, MONETIZATION_ARCHITECTURE.md
 * - Read-only scaffold for coach/pro workout template creation
 * - No writes, no competitive engine interaction
 * - Templates are coach-created workout plans assignable to clients
 * - Uses V1 lift pool as exercise catalog
 */

import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { V1_LIFT_POOL, V1_LIFT_LABELS } from '@/lib/lifts/canonical';
import PaywallGate from '@/components/PaywallGate';
import { BUILT_IN_TEMPLATES } from '@/lib/builtInTemplates';

export default function WorkoutTemplatesScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const templates = BUILT_IN_TEMPLATES;

  return (
    <PaywallGate requiredTier="coach_pro">
      <ScrollView style={[styles.container, { backgroundColor: c.background }]}>
        <Text style={[styles.title, { color: c.accent }]}>WORKOUT TEMPLATES</Text>
        <Text style={[styles.subtitle, { color: c.textSecondary }]}>
          Coach workout plans (scaffold — read-only)
        </Text>

        {/* Exercise catalog */}
        <View style={[styles.section, { borderColor: c.border }]}>
          <Text style={[styles.sectionTitle, { color: c.text }]}>V1 Exercise Catalog</Text>
          <View style={styles.chipRow}>
            {V1_LIFT_POOL.map((lift) => (
              <View key={lift} style={[styles.chip, { backgroundColor: c.surface, borderColor: c.border }]}>
                <Text style={[styles.chipText, { color: c.text }]}>{V1_LIFT_LABELS[lift]}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Template list */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: c.text }]}>
            Templates ({templates.length})
          </Text>

          {templates.map((tmpl) => (
            <View
              key={tmpl.id}
              style={[styles.templateCard, { backgroundColor: c.surface, borderColor: c.border }]}
            >
              <Text style={[styles.templateName, { color: c.text }]}>{tmpl.name}</Text>
              <Text style={[styles.templateDesc, { color: c.textSecondary }]}>
                {tmpl.description}
              </Text>

              <View style={styles.exerciseList}>
                {tmpl.exercises.map((ex, i) => (
                  <View key={`${tmpl.id}-${i}`} style={styles.exerciseRow}>
                    <Text style={[styles.exerciseName, { color: c.accent }]}>
                      {ex.exerciseName}
                    </Text>
                    <Text style={[styles.exerciseDetail, { color: c.textSecondary }]}>
                      {ex.targetSets} × {ex.targetReps}
                      {ex.notes ? ` · ${ex.notes}` : ''}
                    </Text>
                  </View>
                ))}
              </View>

              <Text style={[styles.templateDate, { color: c.textTertiary }]}>
                {tmpl.category}
              </Text>
            </View>
          ))}
        </View>

        {/* Scaffold notice */}
        <View style={[styles.scaffoldNotice, { backgroundColor: c.surfaceVariant }]}>
          <Text style={[styles.scaffoldText, { color: c.textTertiary }]}>
            This is a read-only scaffold. Template creation, editing, assignment to clients,
            and persistence will be implemented in a future phase.
          </Text>
        </View>
      </ScrollView>
    </PaywallGate>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, paddingTop: 60 },
  title: { fontSize: 24, fontWeight: '900', letterSpacing: 2, marginBottom: 4 },
  subtitle: { fontSize: 14, marginBottom: 20 },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 16, fontWeight: '700', marginBottom: 12 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 2,
    borderWidth: 1,
  },
  chipText: { fontSize: 13, fontWeight: '600' },
  templateCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
  },
  templateName: { fontSize: 18, fontWeight: '700', marginBottom: 4 },
  templateDesc: { fontSize: 13, marginBottom: 12 },
  exerciseList: { gap: 8, marginBottom: 12 },
  exerciseRow: {},
  exerciseName: { fontSize: 14, fontWeight: '700' },
  exerciseDetail: { fontSize: 13 },
  templateDate: { fontSize: 11, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined },
  scaffoldNotice: {
    padding: 16,
    borderRadius: 2,
    marginBottom: 40,
  },
  scaffoldText: { fontSize: 12, textAlign: 'center', fontStyle: 'italic' },
});
